/**
 * Aiká — Carrossel de reels centralizado, com loop contínuo de verdade
 *
 * Em vez de "pular" de volta pro primeiro card ao passar do último
 * (o que causaria um salto visual), o conjunto de cards é clonado
 * várias vezes no DOM, criando um buffer nos dois lados. Sempre que a
 * navegação se aproxima da borda desse buffer, o carrossel se
 * reposiciona silenciosamente (sem transição) para uma cópia
 * equivalente mais ao centro — como o card é visualmente idêntico,
 * ninguém percebe o reposicionamento, e a impressão é de um loop
 * contínuo e infinito, sempre seguindo pro mesmo lado.
 */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-reels-carousel]').forEach((root) => {
    const viewport = root.querySelector('[data-reels-viewport]');
    const track = root.querySelector('[data-reels-track]');
    const originalCards = Array.from(root.querySelectorAll('[data-reels-card]'));
    const prevBtn = root.querySelector('[data-reels-prev]');
    const nextBtn = root.querySelector('[data-reels-next]');

    if (!viewport || !track || !originalCards.length) return;

    const realCount = originalCards.length;

    // Com 1 card só não existe "lado" nenhum pra navegar — só toca ele.
    if (realCount <= 1) {
      const card = originalCards[0];
      card.classList.add('is-active');
      const video = card.querySelector('video');
      if (video) video.play().catch(() => {});
      return;
    }

    const REPEAT = 5; // total de cópias completas do conjunto no DOM
    const MIDDLE_COPY = Math.floor(REPEAT / 2);

    // A primeira cópia já é o conteúdo original renderizado pelo Liquid;
    // aqui só adicionamos as cópias extras clonando esse conjunto.
    for (let copy = 1; copy < REPEAT; copy += 1) {
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('id');
        track.appendChild(clone);
      });
    }

    const allCards = Array.from(root.querySelectorAll('[data-reels-card]'));
    const allVideos = allCards.map((card) => card.querySelector('video'));

    let activeIndex = MIDDLE_COPY * realCount + Math.floor(realCount / 2);

    const getGap = () => {
      const trackStyles = getComputedStyle(track);
      const value = parseFloat(trackStyles.columnGap || trackStyles.gap);
      return Number.isNaN(value) ? 0 : value;
    };

    const getCardWidths = () => {
      const styles = getComputedStyle(root);
      const normalWidth = parseFloat(styles.getPropertyValue('--aika-reels-card-width')) || 190;
      const activeWidth = parseFloat(styles.getPropertyValue('--aika-reels-active-width')) || 280;
      return { normalWidth, activeWidth };
    };

    const applyPosition = (animate) => {
      const gap = getGap();
      const { normalWidth, activeWidth } = getCardWidths();

      if (!animate) {
        // Suprime a transição do track E dos cards — sem isso, mesmo com
        // o track pulando instantaneamente, o card que troca de "ativo"
        // ainda animaria a largura por conta própria, causando o engasgo.
        track.style.transition = 'none';
        allCards.forEach((card) => {
          card.style.transition = 'none';
        });
      }

      allCards.forEach((card, i) => {
        card.classList.toggle('is-active', i === activeIndex);
      });

      // Larguras conhecidas (normal ou ativa) — sem medir cada card
      // individualmente, evitando forçar recálculo de layout repetido.
      let offset = 0;
      let activeCenter = 0;
      allCards.forEach((card, i) => {
        const width = i === activeIndex ? activeWidth : normalWidth;
        if (i === activeIndex) {
          activeCenter = offset + width / 2;
        }
        offset += width + gap;
      });

      const viewportWidth = viewport.getBoundingClientRect().width;
      const translateX = viewportWidth / 2 - activeCenter;
      track.style.transform = `translateX(${translateX}px)`;

      if (!animate) {
        // Força o navegador a aplicar tudo isso antes de religar as
        // transições, pra nada disso ser animado.
        // eslint-disable-next-line no-unused-expressions
        track.getBoundingClientRect();
        track.style.transition = '';
        allCards.forEach((card) => {
          card.style.transition = '';
        });
      }

      allVideos.forEach((video, i) => {
        if (!video) return;
        if (i === activeIndex) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    };

    const recenterIfNeeded = () => {
      const copyIndex = Math.floor(activeIndex / realCount);
      if (copyIndex !== MIDDLE_COPY) {
        const withinCopy = activeIndex % realCount;
        activeIndex = MIDDLE_COPY * realCount + withinCopy;
        applyPosition(false);
      }
    };

    const goTo = (index) => {
      activeIndex = index;
      applyPosition(true);
    };

    track.addEventListener('transitionend', (event) => {
      if (event.target === track && event.propertyName === 'transform') {
        recenterIfNeeded();
      }
    });

    const bindCardEvents = (card, index) => {
      card.addEventListener('click', (event) => {
        if (index !== activeIndex) {
          event.preventDefault();
          event.stopPropagation();
          goTo(index);
        }
      });

      const muteBtn = card.querySelector('[data-reels-mute]');
      if (muteBtn) {
        muteBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          const video = card.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          card.classList.toggle('is-unmuted', !video.muted);
        });
      }
    };

    allCards.forEach((card, i) => bindCardEvents(card, i));

    if (prevBtn) {
      prevBtn.addEventListener('click', () => goTo(activeIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => goTo(activeIndex + 1));
    }

    applyPosition(false);

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => applyPosition(false), 150);
    });
  });
});
